import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NotificationBell } from './app.notification-bell';
import { ProfileService } from '@/pages/my-profile/profile.service';
import { AuthService } from '@/pages/service/core/auth.service';
import { hasAnyRole } from './role.util';

/**
 * Top-right account cluster shown on every page: the notification bell + a profile-avatar
 * button whose dropdown holds My Profile / Settings / Logout. Fixed to the top-right corner
 * — a TOP-anchored fixed element renders reliably under the app's display-scaling zoom
 * (a bottom-anchored one gets pushed off-screen). Settings + Logout moved here from the
 * sidebar; the avatar shows the user's uploaded photo (initials fallback).
 */
@Component({
    selector: 'app-user-menu',
    standalone: true,
    imports: [CommonModule, NotificationBell],
    template: `
    <div class="um" (click)="$event.stopPropagation()">
        <app-notification-bell variant="topbar"></app-notification-bell>

        <button class="um__avatar" (click)="toggle()" [title]="name || 'Account'" aria-label="Account">
            <img *ngIf="avatarUrl" [src]="avatarUrl" alt="Profile" />
            <span *ngIf="!avatarUrl" class="um__initials">{{ initials() }}</span>
        </button>

        <div class="um__panel" *ngIf="open">
            <div class="um__head">
                <div class="um__avatar um__avatar--lg">
                    <img *ngIf="avatarUrl" [src]="avatarUrl" alt="Profile" />
                    <span *ngIf="!avatarUrl" class="um__initials">{{ initials() }}</span>
                </div>
                <div class="um__id">
                    <div class="um__name">{{ name || 'My account' }}</div>
                    <div class="um__email" *ngIf="email">{{ email }}</div>
                </div>
            </div>
            <button *ngIf="!isSuper" class="um__item" (click)="go(['/settings'], { tab: 'profile' })"><i class="pi pi-user"></i> My Profile</button>
            <button *ngIf="!isSuper" class="um__item" (click)="go(['/settings'])"><i class="pi pi-cog"></i> Settings</button>
            <div class="um__sep"></div>
            <button class="um__item um__item--danger" (click)="logout()"><i class="pi pi-sign-out"></i> Logout</button>
        </div>
    </div>`,
    styles: [`
        .um { position: relative; display: inline-flex; align-items: center; gap: 6px; }
        .um__avatar { width: 40px; height: 40px; border-radius: 50%; overflow: hidden; border: 2px solid var(--surface-card, var(--gh-surface)); cursor: pointer; background: var(--primary-color, var(--gh-primary-bright)); color: var(--gh-on-primary); display: inline-flex; align-items: center; justify-content: center; padding: 0; box-shadow: 0 2px 10px rgba(15,23,42,0.18); }
        .um__avatar img { width: 100%; height: 100%; object-fit: cover; }
        .um__initials { font-size: var(--gh-fs-15); font-weight: 700; }
        .um__panel { position: absolute; top: calc(100% + 10px); right: 0; width: 250px; background: var(--surface-card, var(--gh-surface)); border: 1px solid var(--surface-border, var(--gh-border)); border-radius: var(--gh-r-lg); box-shadow: 0 16px 48px rgba(15,23,42,0.20); overflow: hidden; z-index: 1201; }
        .um__head { display: flex; align-items: center; gap: 10px; padding: 14px; border-bottom: 1px solid var(--surface-border, var(--gh-border)); }
        .um__avatar--lg { width: 44px; height: 44px; box-shadow: none; border: none; flex-shrink: 0; }
        .um__id { min-width: 0; }
        .um__name { font-weight: 700; font-size: var(--gh-fs-14); color: var(--text-color, var(--gh-text)); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .um__email { font-size: var(--gh-fs-12); color: var(--text-color-secondary, var(--gh-text-3)); word-break: break-all; }
        .um__item { display: flex; align-items: center; gap: 10px; width: 100%; padding: 11px 14px; border: none; background: transparent; text-align: left; cursor: pointer; font-size: var(--gh-fs-14); color: var(--text-color, var(--gh-text)); }
        .um__item:hover { background: var(--surface-hover, var(--gh-surface-sunken)); }
        .um__item i { font-size: var(--gh-fs-14); color: var(--text-color-secondary, var(--gh-text-3)); }
        .um__item--danger { color: var(--gh-danger); }
        .um__item--danger i { color: var(--gh-danger); }
        .um__sep { height: 1px; background: var(--surface-border, var(--gh-surface-sunken)); }
    `]
})
export class UserMenu implements OnInit, OnDestroy {
    private profileSvc = inject(ProfileService);
    private auth = inject(AuthService);
    private router = inject(Router);

    open = false;
    name = '';
    email = '';
    avatarUrl: string | null = null;
    readonly isSuper = hasAnyRole('ROLE_SUPER_ADMIN');
    private readonly docClick = () => { if (this.open) this.open = false; };

    ngOnInit(): void {
        // Super admins have no company profile — skip the fetch (it would 403); they still get Logout.
        if (!this.isSuper) this.load();
        document.addEventListener('click', this.docClick);
    }
    ngOnDestroy(): void {
        document.removeEventListener('click', this.docClick);
        if (this.avatarUrl) { try { URL.revokeObjectURL(this.avatarUrl); } catch { /* ignore */ } }
    }

    private load(): void {
        this.profileSvc.me().subscribe({
            next: (res: any) => {
                const p = res?.data ?? res ?? {};
                this.name = p?.name || '';
                this.email = p?.email || '';
                if (p?.profileImageUrl) this.loadAvatar(p.profileImageUrl);
            },
            error: () => { /* leave initials */ },
        });
    }
    private loadAvatar(path: string): void {
        this.profileSvc.avatar(path).subscribe({
            next: (blob) => {
                if (this.avatarUrl) { try { URL.revokeObjectURL(this.avatarUrl); } catch { /* ignore */ } }
                this.avatarUrl = URL.createObjectURL(blob as Blob);
            },
            error: () => { /* leave initials */ },
        });
    }
    initials(): string {
        const n = (this.name || '').trim();
        return n ? n.split(/\s+/).map((w: string) => w[0]).slice(0, 2).join('').toUpperCase() : 'U';
    }
    toggle(): void { this.open = !this.open; }
    go(link: any[], queryParams?: any): void {
        this.open = false;
        this.router.navigate(link, queryParams ? { queryParams } : {});
    }
    logout(): void {
        this.open = false;
        this.auth.logout();
        this.router.navigate(['/auth/login']);
    }
}
